import { rememberRegistrationToken } from '../utils/registrationSession';
import axios from 'axios';
import {
  referralToken,
  onboardingCreationNonce,
  documentRequestOptions,
  documentIdentity,
  rememberOnboardingToken,
  onboardingPayload,
  onboardingIdentity,
  onboardingRequestOptions
} from '../utils/upestateReferral';
import { useQuery } from '@tanstack/react-query';

const API = `${process.env.REACT_APP_API_BASE_URL}/passport`;

// useUser's interceptor resolves non-401 HTTP errors. Check status here so a denied
// capability can never be treated as a completed onboarding step. Preserve the stage
// forms' existing 400 field-validation contract without retaining axios request secrets.
const onboardingResponse = async (request, { validation = false, businessErrors = false } = {}) => {
  let response;
  try {
    response = await request();
  } catch (error) {
    if (!error?.response) throw new Error('Passport onboarding request unavailable');
    response = error.response;
  }
  // These exact messages are the shared capability verifier's400 contract. Keep
  // field-validation responses intact; an expired proof needs session recovery UI.
  const invalidSession =
    response.status === 400 &&
    [
      'Invalid referral capability',
      'Expired or invalid referral capability',
      'Invalid onboarding session',
      'Invalid referral'
    ].includes(response.data?.message);
  if (validation && response.status === 400 && !invalidSession) return response;
  const knownBusinessError =
    businessErrors &&
    ['USER_ALREADY_EXISTS', 'STAGE1_NOT_COMPLETED', 'ACCOUNT_NOT_CREATED'].includes(
      response.data?.error?.reason
    );
  if (
    response.status >= 200 &&
    response.status < 300 &&
    (response.data?.success !== false || knownBusinessError)
  ) {
    return response;
  }
  const error = new Error('Passport onboarding request failed');
  error.status = response.status;
  if (invalidSession) error.code = 'ONBOARDING_SESSION_INVALID';
  error.response = { status: response.status, data: response.data };
  throw error;
};

export const getFxData = async (authCode) => {
  const { data } = await onboardingResponse(() =>
    axios.get(`${API}/onboarding/fx/${authCode}`, documentRequestOptions(authCode))
  );

  return data;
};

export const useOnboardingPassport = (email, { enabled }) => {
  const query = useQuery({
    queryKey: ['onboardingPassport', onboardingIdentity(email)],
    queryFn: async () => {
      const { data } = await onboardingResponse(
        () =>
          axios.get(
            `${API}/onboarding/${encodeURIComponent(email)}`,
            onboardingRequestOptions(email)
          ),
        { validation: true, businessErrors: true }
      );
      return data;
    },
    enabled,
    staleTime: 0,
    gcTime: 0
  });
  return { ...query, data: query.isError ? undefined : query.data };
};

export const useOnboardingPassportDocuments = (authCode, { enabled }) => {
  const query = useQuery({
    queryKey: ['useOnboardingPassportDocuments', documentIdentity(authCode)],
    queryFn: async () => {
      const { data } = await onboardingResponse(() =>
        axios.get(`${API}/onboarding/documents/${authCode}`, documentRequestOptions(authCode))
      );
      return data;
    },
    enabled,
    staleTime: 0,
    gcTime: 0
  });
  return { ...query, data: query.isError ? undefined : query.data };
};

export const usePassportRequests = () =>
  useQuery({
    queryKey: ['passportRequests'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/requests`);

      return data;
    }
  });

export const usePassportOffers = () =>
  useQuery({
    queryKey: ['passportOffers'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/offers`);

      return data;
    }
  });

export const savePassportRequest = async (data) => {
  const resp = await axios.post(`${API}/request`, data);

  return resp.data;
};

export const updatePassportRequest = async (passportRequestId, data) => {
  const resp = await axios.patch(`${API}/request/${passportRequestId}`, data);

  return resp.data;
};

export const savePassportOnboardingsDocuments = async (authCode, data) => {
  const resp = await onboardingResponse(() =>
    axios.post(`${API}/onboarding/documents/${authCode}`, data, documentRequestOptions(authCode))
  );

  return resp.data;
};

export const submitPassportOnboardingsDocuments = async (authCode) => {
  const resp = await onboardingResponse(() =>
    axios.post(
      `${API}/onboarding/documents/${authCode}/submit`,
      {},
      documentRequestOptions(authCode)
    )
  );

  return resp.data;
};

export const getPassportRequest = async (email) => {
  const { data } = await onboardingResponse(
    () =>
      axios.get(`${API}/onboarding/${encodeURIComponent(email)}`, onboardingRequestOptions(email)),
    { validation: true, businessErrors: true }
  );

  return data;
};

export const createPutPresignedUrl = async (fileExtension) => {
  const resp = await axios.get(`${API}/documents/put-presigned-url?fileExtension=${fileExtension}`);

  return resp.data;
};

export const createGetPresignedUrl = async (key) => {
  const resp = await axios.get(`${API}/documents/get-presigned-url?key=${key}`);

  return resp.data;
};

export const trackPassportRequestProgress = async (requestId, key, value = true) => {
  const resp = await axios.post(`${API}/request/${requestId}/track/progress`, {
    [key]: value
  });

  return resp.data;
};

export const createOnboardingPutPresignedUrl = (authCode) => async (fileExtension) => {
  const resp = await onboardingResponse(() =>
    axios.get(
      `${API}/onboarding/documents/${authCode}/put-presigned-url?fileExtension=${encodeURIComponent(fileExtension)}`,
      documentRequestOptions(authCode)
    )
  );

  return resp.data;
};

export const createOnboardingGetPresignedUrl = (authCode) => async (key) => {
  const resp = await onboardingResponse(() =>
    axios.get(
      `${API}/onboarding/documents/${authCode}/get-presigned-url?key=${encodeURIComponent(key)}`,
      documentRequestOptions(authCode)
    )
  );

  return resp.data;
};

export const requestPassportOffer = async (data) => await axios.post(`${API}/offers/request`, data);

export const updatePassportOnboardingRequest = async (email, data) =>
  onboardingResponse(() =>
    axios.patch(`${API}/onboarding/${encodeURIComponent(email)}`, onboardingPayload(email, data))
  );

export const createPassportOnboardingStage1Request = async (data) => {
  const token = referralToken();
  const response = await onboardingResponse(
    () =>
      axios.post(`${API}/onboarding/stage-1`, {
        ...data,
        ...(token
          ? {
              upestate_referral_token: token,
              upestate_creation_nonce: onboardingCreationNonce(data.email)
            }
          : {})
      }),
    { validation: true }
  );
  if (response.status >= 200 && response.status < 300) {
    rememberOnboardingToken(data.email, response.data?.data?.upestate_onboarding_token);
  }
  return response;
};

export const savePassportOnboardingStage2Request = async (data) =>
  onboardingResponse(
    () => axios.post(`${API}/onboarding/stage-2`, onboardingPayload(data.email, data)),
    { validation: true }
  );

export const transferOnboardingRequestToAccount = async (data) => {
  const response = await onboardingResponse(() =>
    axios.post(`${API}/onboarding/account`, onboardingPayload(data.email, data))
  );
  rememberRegistrationToken(data.email, response?.data?.data?.registration_token);
  return response;
};

// Hides the selected uploaded documents from the app. The server keeps the
// files; `data` is `{ request_id, document_ids: string[] }`.
export const removePassportDocuments = async (data) =>
  await axios.post(`${API}/onboarding/documents/remove`, data);

export const usePropertyRecommendations = (requestId, options = {}) =>
  useQuery({
    queryKey: ['propertyRecommendations', requestId],
    queryFn: async () => {
      try {
        return (await axios.get(`${API}/${requestId}/recommendations/properties`))?.data ?? null;
      } catch (error) {
        console.error('Error fetching property recommendations:', error);
      }
    },
    enabled: !!requestId && options.enabled !== false,
    retry: 1,
    ...options
  });
